/**
 * Supported app locales for V1.
 * Spanish dialects: Rioplatense (es-AR), Latin American (es-419), Castilian (es-ES).
 */

export const APP_LOCALES = ['en', 'es-AR', 'es-419', 'es-ES', 'it'] as const;

export type AppLocale = (typeof APP_LOCALES)[number];

export const DEFAULT_LOCALE: AppLocale = 'en';

export const PREFERRED_LANGUAGE_STORAGE_KEY = 'we-tree:ui:preferredLanguage';

/** Native / endonym labels for the language selector */
export const LOCALE_LABELS: Record<AppLocale, string> = {
  en: 'English',
  'es-AR': 'Español (Rioplatense)',
  'es-419': 'Español (Latinoamérica)',
  'es-ES': 'Español (España)',
  it: 'Italiano'
};

export function isAppLocale(value: string | null | undefined): value is AppLocale {
  return value != null && (APP_LOCALES as readonly string[]).includes(value);
}

/**
 * Map a BCP-47 / navigator language tag onto a supported app locale.
 */
export function mapBrowserLanguage(tag: string): AppLocale | null {
  const normalized = tag.trim().replace(/_/g, '-');
  if (!normalized) return null;

  const lower = normalized.toLowerCase();

  if (lower === 'en' || lower.startsWith('en-')) return 'en';
  if (lower === 'it' || lower.startsWith('it-')) return 'it';

  if (lower === 'es-ar' || lower.startsWith('es-ar-') || lower === 'es-uy' || lower.startsWith('es-uy-')) {
    return 'es-AR';
  }
  if (lower === 'es-es' || lower.startsWith('es-es-')) return 'es-ES';
  if (lower === 'es' || lower.startsWith('es-') || lower === 'es-419') return 'es-419';

  return null;
}

export function resolveLocaleFromBrowser(languages: readonly string[] = []): AppLocale {
  for (const tag of languages) {
    const mapped = mapBrowserLanguage(tag);
    if (mapped) return mapped;
  }
  return DEFAULT_LOCALE;
}

export function readStoredPreferredLanguage(): AppLocale | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(PREFERRED_LANGUAGE_STORAGE_KEY);
    return isAppLocale(stored) ? stored : null;
  } catch {
    return null;
  }
}

export function writeStoredPreferredLanguage(locale: AppLocale): void {
  try {
    localStorage.setItem(PREFERRED_LANGUAGE_STORAGE_KEY, locale);
  } catch {
    // ignore quota / private mode
  }
}

/**
 * Resolve active locale: explicit preference → browser → English.
 */
export function resolveAppLocale(preferred: AppLocale | null | undefined): AppLocale {
  if (isAppLocale(preferred)) return preferred;
  if (typeof navigator !== 'undefined') {
    return resolveLocaleFromBrowser(navigator.languages?.length ? navigator.languages : [navigator.language]);
  }
  return DEFAULT_LOCALE;
}

/** Amplify I18n language code (dialects collapse to `es`). */
export function amplifyLanguageForLocale(locale: AppLocale): string {
  if (locale === 'it') return 'it';
  if (locale.startsWith('es')) return 'es';
  return 'en';
}
